<!-- eslint-disable max-len -->
<template>
  <div class="hero-slider-container">
    <Splide
      ref="splide"
      :has-track="false"
      :options="splideOptions"
    >
      <SplideTrack>
        <SplideSlide
          v-for="slide in slides"
          :key="slide.id"
          class="hero-slide"
        >
          <img
            v-if="slide.background"
            :src="slide.background"
            alt=""
          >
          <div class="container">
            <div
              class="slide-content"
            >
              <h2>
                {{ slide.title }}
              </h2>
              <p v-if="slide.text">
                {{ slide.text }}
              </p>
              <a
                v-if="slide.button_text"
                :href="slide.button_link"
                class="btn-custom primary"
              >
                {{ slide.button_text }}
              </a>
            </div>
          </div>
        </SplideSlide>
      </SplideTrack>

      <div class="slider-navigation">
        <div class="container">
          <div
            ref="lighthouseRef"
            class="lighthouse"
          >
            <svg
              width="95"
              height="95"
              viewBox="0 0 95 95"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 47.5C0 21.2665 21.2665 0 47.5 0C73.7335 0 95 21.2665 95 47.5V75H0V47.5Z"
                fill="#FEC327"
                fill-opacity="0.5"
              />
              <path
                d="M15 57.5C15 39.5507 29.5507 25 47.5 25C65.4493 25 80 39.5507 80 57.5V75H15V57.5Z"
                fill="#FEC327"
              />
              <g clip-path="url(#clip0_8098_2680)">
                <path
                  d="M60.2761 66.252C59.7047 65.472 58.4379 65.628 57.2066 65.628C57.2066 65.628 58.4321 51.252 50.4547 48.756C50.4547 46.884 49.842 45.006 47.9979 45.006H46.9963C45.1581 45.006 44.5395 46.878 44.5395 48.756C36.568 51.252 37.7935 65.628 37.7935 65.628C36.568 65.628 35.2954 65.478 34.7239 66.252C33.6634 67.686 36.5621 71.25 39.019 75H55.9811C58.4379 71.25 61.3367 67.686 60.2761 66.252ZM54.249 64.29C54.249 65.028 53.6598 65.628 52.9351 65.628H42.0591C41.3344 65.628 40.7452 65.028 40.7452 64.29V58.836C40.7452 58.098 41.3344 57.498 42.0591 57.498H52.9351C53.6598 57.498 54.249 58.098 54.249 58.836V64.29Z"
                  fill="white"
                />
              </g>
              <rect
                width="95"
                height="20"
                transform="translate(0 75)"
                fill="#FEC327"
                fill-opacity="0.5"
              />
              <rect
                width="65"
                height="20"
                transform="translate(15 75)"
                fill="#FEC327"
              />
              <rect
                width="17"
                height="20"
                transform="translate(39 75)"
                fill="white"
              />
              <defs>
                <clipPath id="clip0_8098_2680">
                  <rect
                    width="26"
                    height="30"
                    fill="white"
                    transform="translate(34.5 45)"
                  />
                </clipPath>
              </defs>
            </svg>

            <div class="lighthouse__left" />
            <div
              class="lighthouse__right"
              :style="{ width: rightBeamWidth + 'px' }"
            />
          </div>

          <div class="nav-buttons">
            <button
              v-for="(slide, index) in slides"
              :key="slide.id"
              :ref="el => navRefs[index] = el"
              class="nav-buttons__button"
              @click="goToSlide(index)"
            >
              {{ slide.navigation_title || `Slide ${index + 1}` }}
            </button>
          </div>
        </div>
      </div>
    </Splide>
    <RegisterLoginSection />
  </div>
</template>

<script>
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import RegisterLoginSection from './RegisterLoginSection.vue';

export default {
  components: {
    Splide,
    SplideSlide,
    SplideTrack,
    RegisterLoginSection,
  },
  props: {
    slides: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      splideOptions: {
        type: 'loop',
        perPage: 1,
        autoplay: true,
        arrows: false,
        pagination: false,

        breakpoints: {
          860: {
            arrows: true,
            pagination: true,
          },
        },
      },
      rightBeamWidth: 0,
      navRefs: [],
      currentIndex: 0,
    };
  },
  mounted() {
    if (!this.$refs.splide?.splide) {
      console.warn('Splide instance not found');
      return;
    }

    const { splide } = this.$refs.splide;
    splide.on('autoplay:playing', (rate) => {
      this.updateRightBeam(rate);
    });
    splide.on('move', (newIndex) => {
      this.currentIndex = newIndex;
      this.updateRightBeam(0);
    });
    // Initial update
    this.updateRightBeam(0);
  },
  methods: {
    goToSlide(index) {
      this.$refs.splide.splide.go(index);
    },
    updateRightBeam(progress) {
      // Safety check - ensure we have all required DOM elements
      if (!this.$refs.lighthouseRef || !this.navRefs[this.currentIndex]) {
        this.rightBeamWidth = 0;
        return;
      }

      // Get the positions of all elements relative to the container
      const lighthouseRect = this.$refs.lighthouseRef.getBoundingClientRect();
      const containerRect = this.$refs.lighthouseRef.parentElement.getBoundingClientRect();
      const currentButtonRect = this.navRefs[this.currentIndex].getBoundingClientRect();

      // Calculate the base positions
      // lighthouseRight: where the lighthouse ends
      // currentButtonLeft: where the current navigation button begins
      const lighthouseRight = lighthouseRect.right - containerRect.left;
      const currentButtonLeft = currentButtonRect.left - containerRect.left;

      // Special handling for the last slide
      if (this.currentIndex === this.slides.length - 1) {
        // On the last slide, we want the beam to grow all the way to the end of navigation
        const navContainerRect = this.navRefs[0].parentElement.getBoundingClientRect();
        const navContainerRight = navContainerRect.right - containerRect.left;

        // baseWidth: distance from lighthouse to current button
        // additionalWidth: remaining distance to end of navigation plus extra padding,
        // scaled by progress
        const baseWidth = currentButtonLeft - lighthouseRight;
        const extraPadding = 30; // Extra distance beyond the last button
        const additionalWidth = ((navContainerRight - currentButtonLeft + extraPadding) * progress);
        this.rightBeamWidth = Math.max(0, baseWidth + additionalWidth);
      } else {
        // For all other slides, grow to the next button
        const nextButtonRect = this.navRefs[this.currentIndex + 1].getBoundingClientRect();
        const nextButtonLeft = nextButtonRect.left - containerRect.left;

        // baseWidth: distance from lighthouse to current button
        // additionalWidth: distance to next button, scaled by progress
        const baseWidth = currentButtonLeft - lighthouseRight;
        const additionalWidth = (nextButtonLeft - currentButtonLeft) * progress;
        this.rightBeamWidth = Math.max(0, baseWidth + additionalWidth);
      }
    },
  },
};
</script>
